import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../../features/auth/services/auth.service';
import { AuthInitializerService } from '../../features/auth/services/auth-initializer.service';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Guard that protects routes requiring admin access
 * Redirects to unauthorized page if not admin
 */
export const adminGuard: CanActivateFn = (route, state) => {
  const showDebugInfo = environment.showDebugInfo;
  
  if (showDebugInfo) {
    console.log('Admin guard activated for route:', state.url);
  }
  
  const router = inject(Router);
  const authService = inject(AuthService);
  const authInitializer = inject(AuthInitializerService);
  
  // Check if auth is ready
  if (!authInitializer.isAuthReady()) {
    if (showDebugInfo) {
      console.log('Admin guard: Auth not ready, waiting for initialization');
    }
    // Wait for auth to be ready
    return of(authInitializer.initializeAuth()).pipe(
      switchMap(() => checkAdminAccess(authService, router, showDebugInfo))
    );
  }
  
  return checkAdminAccess(authService, router, showDebugInfo);
};

/**
 * Helper function to check admin access
 */
function checkAdminAccess(authService: AuthService, router: Router, showDebugInfo: boolean): Observable<boolean> {
  // First check if the user is authenticated
  return authService.checkAuthenticated().pipe(
    take(1),
    switchMap(isAuthenticated => {
      if (!isAuthenticated) {
        if (showDebugInfo) {
          console.log('Admin guard: User not authenticated, redirecting to login');
        }
        router.navigate(['/login']);
        return of(false);
      }
      
      // Then check if the user is an admin
      return authService.isAdmin().pipe(
        take(1),
        map(isAdmin => {
          if (showDebugInfo) {
            console.log('Admin guard check result:', isAdmin);
          }
          
          if (isAdmin) {
            if (showDebugInfo) {
              console.log('Admin guard: Access granted - user is admin');
            }
            return true;
          }
          
          if (showDebugInfo) {
            console.log('Admin guard: Access denied - user is not admin');
          }
          router.navigate(['/unauthorized']);
          return false;
        })
      );
    })
  );
} 